import React from "react";
import Quill from "quill";
import QuillImageDropAndPaste from "quill-image-drop-and-paste";
import "quill/dist/quill.snow.css";
import {
  Box,
  Typography,
  useTheme,
  Modal,
  LinearProgress,
} from "@mui/material";
import { QuillEditorProps } from "./types";
import { useQuillEditor } from "./useQuillEditor";

Quill.register("modules/imageDropAndPaste", QuillImageDropAndPaste);

const QuillEditor: React.FC<QuillEditorProps> = ({
  value = "",
  onChange,
  onBlur,
  onImageUpload,
  placeholder = "",
  readOnly = false,
  theme: editorTheme = "snow",
  modules: customModules,
  formats: customFormats,
  height = 200,
  label,
  hasError = false,
  messageError,
  isDisabled = false,
  maxLength,
  showCharCount = false,
  className,
  sx,
  maxImgWidth = "50%",
}) => {
  const theme = useTheme();
  const { editorRef, modalImage, setModalImage, charCount, isImageUploading } =
    useQuillEditor({
      value,
      onChange,
      onBlur,
      onImageUpload,
      placeholder,
      readOnly,
      theme: editorTheme,
      modules: customModules,
      formats: customFormats,
      maxLength,
      isDisabled,
    });

  return (
    <Box sx={{ width: "100%", ...sx }} className={className}>
      {label && (
        <Typography
          fontSize={14}
          fontWeight={700}
          color="shadesOfDark.dark"
          mb={0.5}
          fontFamily="Open Sans"
        >
          {label}
        </Typography>
      )}
      <Box
        onPaste={(e) => e.stopPropagation()}
        sx={{
          position: "relative",
          border: hasError
            ? `1px solid ${theme.palette.systemColors.error.main ?? ""}`
            : `1px solid ${theme.palette.shadesOfDark.light}`,
          borderRadius: 1,
          backgroundColor: isDisabled
            ? theme.palette.shadesOfDark?.ultraLight
            : theme.palette.background?.paper,
          opacity: isDisabled || isImageUploading ? 0.7 : 1,
          pointerEvents: isImageUploading ? "none" : "auto",
          "& .ql-container": {
            fontSize: 14,
            fontFamily: "Open Sans",
            height: `${height}px`,
            borderRadius: "0 0 4px 4px",
          },
          "& .ql-editor": {
            minHeight: `${height}px`,
            color: theme.palette.shadesOfDark?.dark,
            "&.ql-blank::before": {
              color: theme.palette.shadesOfDark?.steel,
              fontStyle: "normal",
            },
          },
          "& .ql-toolbar": {
            borderRadius: "4px 4px 0 0",
            backgroundColor: theme.palette.shadesOfDark?.ultraLight,
            borderBottom: `1px solid ${theme.palette.shadesOfDark?.light}`,
          },
          "& .ql-stroke": {
            stroke: theme.palette.shadesOfDark?.dark,
          },
          "& .ql-fill": {
            fill: theme.palette.shadesOfDark?.dark,
          },
          "& .ql-picker-label": {
            color: theme.palette.shadesOfDark?.dark,
          },
          "& .ql-editor img": {
            maxWidth: maxImgWidth,
            height: "auto",
            cursor: "pointer",
          },
        }}
      >
        {isImageUploading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              zIndex: 10,
              borderTopLeftRadius: 4,
              borderTopRightRadius: 4,
              overflow: "hidden",
            }}
          >
            <LinearProgress color="primary" />
          </Box>
        )}
        <div ref={editorRef} />
      </Box>

      <Box display="flex" justifyContent="space-between" mt={0.5}>
        <Box>
          {hasError && messageError && (
            <Typography
              fontFamily="Open Sans"
              fontSize={10}
              color="systemColors.error.main"
            >
              {messageError}
            </Typography>
          )}
        </Box>

        <Box>
          {isImageUploading && (
            <Typography
              fontFamily="Open Sans"
              fontSize={10}
              color="primary.main"
              sx={{ fontStyle: "italic", mr: 1, display: "inline-block" }}
            >
              Enviando imagem...
            </Typography>
          )}

          {showCharCount && maxLength && (
            <Typography
              fontFamily="Open Sans"
              fontSize={10}
              color="shadesOfDark.steel"
              sx={{ display: "inline-block" }}
            >
              {charCount}/{maxLength}
            </Typography>
          )}
        </Box>
      </Box>
      <Modal open={!!modalImage} onClose={() => setModalImage(null)}>
        <Box
          onClick={() => setModalImage(null)}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            maxWidth: "100vw",
            maxHeight: "100vh",
            outline: "none",
          }}
        >
          <img
            src={modalImage || ""}
            alt="Expanded"
            style={{ maxWidth: "100%", maxHeight: "100vh", display: "block" }}
          />
        </Box>
      </Modal>
    </Box>
  );
};

export default QuillEditor;
